/**
 * Ory Message Context Types
 * 
 * Type definitions for Ory message contexts based on official specification:
 * https://github.com/ory/docs/blob/abc29c9fd51176bb7fd8339bc9e5ce3e74b6bec0/docs/kratos/concepts/messages.json
 * 
 * These types ensure compile-time safety when formatting context values for translations.
 * The context types are derived directly from Ory Kratos message definitions.
 */

/**
 * Context for messages with privileged session expiration
 * Used by: RECOVERY_SUCCESSFUL (1060001)
 */
export interface OryPrivilegedSessionContext {
  privilegedSessionExpiresAt: string;  // ISO 8601 timestamp
  privileged_session_expires_at: string;  // ISO 8601 timestamp (snake_case variant)
  privileged_session_expires_at_unix: number;  // Unix timestamp (seconds)
}

/**
 * Context for flow expiration messages
 * Used by: LOGIN_EXPIRED, REGISTRATION_EXPIRED, SETTINGS_EXPIRED, RECOVERY_EXPIRED, VERIFICATION_EXPIRED
 * Message IDs: 4010001, 4040001, 4050001, 4060005, 4070005
 */
export interface OryFlowExpiredContext {
  expired_at: string;  // ISO 8601 timestamp
  expired_at_unix: number;  // Unix timestamp (seconds)
}

/**
 * Context for provider-related messages (OIDC/OAuth)
 * Used by: SIGN_IN_WITH, SIGN_UP_WITH, LINK_GROUP, UNLINK_GROUP
 * Message IDs: 1010002, 1010018, 1040002, 1050002, 1050003
 */
export interface OryProviderContext {
  provider: string;  // Provider name (e.g., "google", "github")
  provider_id?: string;  // Provider ID
}

/**
 * Context for code sending with address/channel
 * Used by: Messages that send codes to specific addresses
 * Message IDs: 1010023
 */
export interface OryAddressChannelContext {
  address: string;  // Email address or phone number
  channel: string;  // Communication channel (e.g., "email", "sms")
}

/**
 * Context for duplicate identifier detection during sign-in/sign-up
 * Used by: DUPLICATE_IDENTIFIER_DETECTED
 * Message IDs: 1010016
 */
export interface OryDuplicateIdentifierContext {
  available_credential_types: string[];  // Available credential types
  available_providers: string[];  // Available OIDC providers
  duplicateIdentifier: string;  // The duplicate identifier (camelCase)
  duplicate_identifier: string;  // The duplicate identifier (snake_case)
  newLoginUrl: string;  // URL for new login (camelCase)
  new_login_url: string;  // URL for new login (snake_case)
  provider: string;  // Provider that triggered the duplicate
}

/**
 * Context for secret display (TOTP, backup codes)
 * Used by: TOTP_SECRET, LOOKUP_SECRET
 * Message IDs: 1050006, 1050009
 */
export interface OrySecretContext {
  secret: string;  // The secret value (base32 for TOTP, recovery code for lookup)
}

/**
 * Context for lookup secret usage tracking
 * Used by: LOOKUP_SECRET_USED
 * Message IDs: 1050014
 */
export interface OrySecretUsedContext {
  used_at: string;  // ISO 8601 timestamp when secret was used
  used_at_unix: number;  // Unix timestamp (seconds) when secret was used
}

/**
 * Context for password validation errors (length)
 * Used by: PASSWORD_TOO_SHORT, PASSWORD_TOO_LONG
 * Message IDs: 4000032, 4000033
 */
export interface OryPasswordLengthContext {
  actual_length: number;  // Actual password length
  min_length?: number;  // Minimum required length (for too short)
  max_length?: number;  // Maximum allowed length (for too long)
}

/**
 * Context for password breach detection
 * Used by: PASSWORD_BREACHED
 * Message IDs: 4000034
 */
export interface OryPasswordBreachContext {
  breaches: number;  // Number of times password found in breaches
}

/**
 * Context for generic system errors
 * Used by: SYSTEM_ERROR
 * Message IDs: 5000001
 */
export interface OryReasonContext {
  reason: string;  // Human-readable error reason
}

/**
 * Context for expected constant validation
 * Used by: Const validation errors
 * Message IDs: 4000029
 */
export interface OryExpectedConstantContext {
  expected: string;  // Expected constant value
}

/**
 * Map message keys to their context types
 * 
 * This is THE single source of truth for type-safe context access.
 * Keys must match those defined in ORY_MESSAGE_ID_TO_KEY.
 * 
 * Only messages that have context should be listed here.
 * If a message doesn't have context, it should NOT be in this map.
 */
export interface OryMessageContextMap {
  // Success messages with context
  RECOVERY_SUCCESS_CHANGE_PASSWORD: OryPrivilegedSessionContext;
  
  // Flow expiration messages
  LOGIN_FLOW_EXPIRED: OryFlowExpiredContext;
  REGISTRATION_FLOW_EXPIRED: OryFlowExpiredContext;
  SETTINGS_FLOW_EXPIRED: OryFlowExpiredContext;
  RECOVERY_FLOW_EXPIRED: OryFlowExpiredContext;
  VERIFICATION_FLOW_EXPIRED: OryFlowExpiredContext;
  
  // Provider-related messages
  SIGN_IN_WITH_PROVIDER_ACTION: OryProviderContext;
  SIGN_UP_WITH_PROVIDER_ACTION: OryProviderContext;
  
  // Address/channel messages
  SEND_CODE_TO_ADDRESS_ACTION: OryAddressChannelContext;
  
  // Duplicate identifier detection
  DUPLICATE_IDENTIFIER_LINK_INFO: OryDuplicateIdentifierContext;
  
  // Secret-related messages
  SECRET_TEXT: OrySecretContext;
  SECRET_TEXT_DUPLICATE: OrySecretContext;
  SECRET_USED_AT_INFO: OrySecretUsedContext;
  
  // Password validation with context
  PASSWORD_TOO_SHORT: OryPasswordLengthContext;
  PASSWORD_TOO_LONG: OryPasswordLengthContext;
  PASSWORD_FOUND_IN_BREACH: OryPasswordBreachContext;
  
  // System errors
  SYSTEM_ERROR: OryReasonContext;
  
  // Validation errors with context
  CONSTANT_MISMATCH: OryExpectedConstantContext;
}

/**
 * Union type of all message keys that have context
 * 
 * Use this to constrain function parameters to only accept
 * message keys that have associated context data.
 * 
 * @example
 * ```typescript
 * function formatContext<T extends OryMessageKeyWithContext>(
 *   key: T,
 *   context: OryMessageContextFor<T>
 * ) { ... }
 * ```
 */
export type OryMessageKeyWithContext = keyof OryMessageContextMap;

/**
 * Type helper to extract context type for a specific message key
 * 
 * @example
 * ```typescript
 * type RecoveryContext = OryMessageContextFor<'RECOVERY_SUCCESSFUL'>;
 * // Result: OryPrivilegedSessionContext
 * ```
 */
export type OryMessageContextFor<T extends OryMessageKeyWithContext> = OryMessageContextMap[T];

/**
 * Type guard to check if a message key has associated context
 * 
 * This narrows the type from `string` to `OryMessageKeyWithContext`,
 * enabling type-safe context access.
 * 
 * @param key - Message key to check
 * @returns True if the key has context, false otherwise
 * 
 * @example
 * ```typescript
 * const key = 'RECOVERY_SUCCESSFUL';
 * if (hasMessageContext(key)) {
 *   // key is now typed as OryMessageKeyWithContext
 *   const ctx: OryMessageContextFor<typeof key> = getContext(key);
 * }
 * ```
 */
export function hasMessageContext(key: string): key is OryMessageKeyWithContext {
  const keysWithContext: readonly string[] = [
    'RECOVERY_SUCCESSFUL',
    'LOGIN_EXPIRED',
    'REGISTRATION_EXPIRED',
    'SETTINGS_EXPIRED',
    'RECOVERY_EXPIRED',
    'VERIFICATION_EXPIRED',
    'SIGN_IN_WITH',
    'SIGN_UP_WITH',
    'SEND_CODE_TO_ADDRESS',
    'DUPLICATE_IDENTIFIER_DETECTED',
    'TOTP_SECRET',
    'LOOKUP_SECRET',
    'LOOKUP_SECRET_USED',
    'PASSWORD_TOO_SHORT',
    'PASSWORD_TOO_LONG',
    'PASSWORD_BREACHED',
    'SYSTEM_ERROR',
    'EXPECTED_CONSTANT',
  ] as const;
  
  return keysWithContext.includes(key);
}

